import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  ChevronLeft, ChevronRight, Bell, Phone, PhoneMissed, Calendar, 
  CheckCircle2, RefreshCw, User, X, BarChart3, PhoneIncoming
} from 'lucide-react';
import axios from 'axios';

// Import des composants originaux
import AlertesConfirmationRDV from './AlertesConfirmationRDV';
import AppelsMissedBanner from './AppelsMissedBanner';
import { UrgentAppelQualiteBanner } from './index';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

// ═══════════════════════════════════════════════════════════════
// MODAL - Pour afficher la bannière originale en grand
// ═══════════════════════════════════════════════════════════════
function BannerModal({ isOpen, onClose, children, title }) {
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-700 bg-slate-800/50">
          <span className="text-sm font-medium text-slate-400">{title}</span>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-400 hover:text-white" />
          </button>
        </div>
        {/* Contenu - La bannière originale */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-60px)]">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE RÉSUMÉE - Pour le carousel
// ═══════════════════════════════════════════════════════════════
function SummaryCard({ icon: Icon, iconColor, bgColor, borderColor, title, count, subtitle, children, onClick }) {
  return (
    <div 
      onClick={onClick}
      className={`flex-shrink-0 w-[280px] ${bgColor} border ${borderColor} rounded-xl p-4 cursor-pointer hover:scale-[1.02] transition-all duration-200`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 ${iconColor} rounded-lg flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-white text-sm truncate">{title}</h3>
            {count !== undefined && count !== null && (
              <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
            )}
          </div>
          {subtitle && <p className="text-xs text-white/70 truncate">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE 1: ALERTES CONFIRMATION RDV
// ═══════════════════════════════════════════════════════════════
function ConfirmationRdvSummary({ collaborateur, onRefresh, onClick }) {
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState({ total: 0, critiques: 0, hautes: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [collaborateur]);

  const loadAlerts = async () => {
    try {
      const url = collaborateur 
        ? `${API}/alertes/confirmation-rdv?collaborateur=${encodeURIComponent(collaborateur)}&include_all=true`
        : `${API}/alertes/confirmation-rdv?include_all=true`;
      const res = await axios.get(url);
      setAlertes(res.data.alertes || []);
      setStats(res.data.stats || { total: 0, critiques: 0, hautes: 0 });
    } catch (e) {
      console.error('Erreur:', e);
    }
    setLoading(false);
  };

  const urgentCount = stats.critiques + stats.hautes;
  const totalCount = alertes.length;

  if (loading) {
    return (
      <SummaryCard icon={Bell} iconColor="bg-amber-500" bgColor="bg-amber-500/10" borderColor="border-amber-500/30" title="Confirmation RDV" subtitle="Chargement...">
        <div className="flex items-center justify-center py-2"><RefreshCw className="w-4 h-4 animate-spin text-amber-400" /></div>
      </SummaryCard>
    );
  }

  return (
    <SummaryCard
      icon={Bell}
      iconColor={totalCount === 0 ? "bg-green-500" : "bg-amber-500"}
      bgColor={totalCount === 0 ? "bg-green-500/10" : "bg-amber-500/10"}
      borderColor={totalCount === 0 ? "border-green-500/30" : "border-amber-500/30"}
      title="Confirmation RDV"
      count={totalCount}
      subtitle={totalCount === 0 ? "Tous confirmés ✓" : urgentCount > 0 ? `${urgentCount} urgent(s)` : 'À confirmer'}
      onClick={onClick}
    >
      {totalCount === 0 ? (
        <div className="text-center py-1">
          <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" />
        </div>
      ) : (
        <div className="space-y-1">
          {alertes.slice(0, 2).map((a, i) => (
            <div key={i} className="text-xs text-white/80 truncate">• {a.client}</div>
          ))}
          {totalCount > 2 && <div className="text-xs text-amber-400">+{totalCount - 2} autres...</div>}
        </div>
      )}
    </SummaryCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE 2: APPELS MANQUÉS
// ═══════════════════════════════════════════════════════════════
function AppelsMissedSummary({ selectedTelepro, onClick }) {
  const [missedData, setMissedData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMissedCalls();
    const interval = setInterval(loadMissedCalls, 30000);
    return () => clearInterval(interval);
  }, [selectedTelepro]);

  const loadMissedCalls = async () => {
    try {
      const url = selectedTelepro 
        ? `${API}/ringover/missed-calls?telepro=${encodeURIComponent(selectedTelepro)}&today_only=true`
        : `${API}/ringover/missed-calls?today_only=true`;
      const res = await axios.get(url);
      setMissedData(res.data);
    } catch (e) {
      console.error('Erreur:', e);
    }
    setLoading(false);
  };

  const totalMissed = missedData?.total_missed || 0;
  const statsByTelepro = missedData?.stats_by_telepro || {};

  if (loading) {
    return (
      <SummaryCard icon={PhoneMissed} iconColor="bg-red-500" bgColor="bg-red-500/10" borderColor="border-red-500/30" title="Appels manqués" subtitle="Chargement...">
        <div className="flex items-center justify-center py-2"><RefreshCw className="w-4 h-4 animate-spin text-red-400" /></div>
      </SummaryCard>
    );
  }

  return (
    <SummaryCard
      icon={PhoneMissed}
      iconColor={totalMissed === 0 ? "bg-slate-500" : "bg-red-500"}
      bgColor={totalMissed === 0 ? "bg-slate-500/10" : "bg-red-500/10"}
      borderColor={totalMissed === 0 ? "border-slate-500/30" : "border-red-500/30"}
      title="Appels manqués"
      count={totalMissed}
      subtitle="Aujourd'hui"
      onClick={onClick}
    >
      {totalMissed === 0 ? (
        <div className="text-center py-1">
          <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" />
        </div>
      ) : (
        <div className="space-y-1">
          {Object.entries(statsByTelepro).slice(0, 2).map(([telepro, data], i) => (
            <div key={i} className="text-xs text-white/80 truncate flex justify-between">
              <span>{telepro}</span>
              <span className="text-red-400 font-bold">{data.count}</span>
            </div>
          ))}
        </div>
      )}
    </SummaryCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE 3: APPELS QUALITÉ À FAIRE
// ═══════════════════════════════════════════════════════════════
function AppelsQualiteSummary({ clients, onClick }) {
  const qualiteAFaire = useMemo(() => {
    if (!clients) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return clients.filter(c => {
      if (!c.dateR1 || c.resultatAppel) return false;
      const parts = c.dateR1.split('/');
      if (parts.length !== 3) return false;
      const dateR1 = new Date(parts[2], parts[1] - 1, parts[0]);
      return dateR1 < today;
    });
  }, [clients]);

  const count = qualiteAFaire.length;

  return (
    <SummaryCard
      icon={Phone}
      iconColor={count === 0 ? "bg-green-500" : "bg-purple-500"}
      bgColor={count === 0 ? "bg-green-500/10" : "bg-purple-500/10"}
      borderColor={count === 0 ? "border-green-500/30" : "border-purple-500/30"}
      title="Appels Qualité"
      count={count}
      subtitle={count === 0 ? "À jour ✓" : "À faire après R1"}
      onClick={onClick}
    >
      {count === 0 ? (
        <div className="text-center py-1">
          <CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" />
        </div>
      ) : (
        <div className="space-y-1">
          {qualiteAFaire.slice(0, 2).map((c, i) => (
            <div key={i} className="text-xs text-white/80 truncate">• {c.nom} {c.prenom}</div>
          ))}
          {count > 2 && <div className="text-xs text-purple-400">+{count - 2} autres...</div>}
        </div>
      )}
    </SummaryCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE 5: EFFICACITÉ APPELS
// ═══════════════════════════════════════════════════════════════
function EfficaciteAppelsSummary({ onClick }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const now = new Date();
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(now);
      monday.setDate(diff);
      const params = new URLSearchParams();
      params.append('start_date', monday.toISOString().split('T')[0]);
      params.append('end_date', now.toISOString().split('T')[0]);
      const res = await axios.get(`${API}/ringover/collaborators/stats?${params}`);
      setStats(res.data);
    } catch (e) {
      console.error('Erreur:', e);
    }
    setLoading(false);
  };

  const totals = stats?.team_totals || {};

  if (loading) {
    return (
      <SummaryCard icon={Phone} iconColor="bg-blue-500" bgColor="bg-blue-500/10" borderColor="border-blue-500/30" title="Efficacité Appels" subtitle="Chargement...">
        <div className="flex items-center justify-center py-2"><RefreshCw className="w-4 h-4 animate-spin text-blue-400" /></div>
      </SummaryCard>
    );
  }

  return (
    <SummaryCard
      icon={Phone}
      iconColor="bg-blue-500"
      bgColor="bg-blue-500/10"
      borderColor="border-blue-500/30"
      title="Efficacité Appels"
      subtitle="Cette semaine"
      onClick={onClick}
    >
      <div className="grid grid-cols-3 gap-1 text-center">
        <div><p className="text-sm font-bold text-blue-400">{totals.outbound_total || 0}</p><p className="text-[10px] text-slate-400">Émis</p></div>
        <div><p className="text-sm font-bold text-green-400">{totals.outbound_rate || 0}%</p><p className="text-[10px] text-slate-400">Décr.</p></div>
        <div><p className="text-sm font-bold text-purple-400">{totals.duration_formatted || '0h'}</p><p className="text-[10px] text-slate-400">Durée</p></div>
      </div>
    </SummaryCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE 6: RDV PRIS
// ═══════════════════════════════════════════════════════════════
function RdvPrisSummary({ clients, onClick }) {
  const stats = useMemo(() => {
    if (!clients) return { total: 0, confirmed: 0, rate: 0 };
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    
    let total = 0, confirmed = 0;
    clients.forEach(c => {
      if (!c.datePriseRdv) return;
      const parts = c.datePriseRdv.split('/');
      if (parts.length !== 3) return;
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      if (date >= monday && date <= now) {
        total++;
        if (c.rdvConfirme?.toLowerCase() === 'oui') confirmed++;
      }
    });
    return { total, confirmed, rate: total > 0 ? Math.round(confirmed / total * 100) : 0 };
  }, [clients]);

  return (
    <SummaryCard
      icon={Calendar}
      iconColor="bg-green-500"
      bgColor="bg-green-500/10"
      borderColor="border-green-500/30"
      title="RDV Pris"
      subtitle="Cette semaine"
      onClick={onClick}
    >
      <div className="grid grid-cols-3 gap-1 text-center">
        <div><p className="text-sm font-bold text-white">{stats.total}</p><p className="text-[10px] text-slate-400">Total</p></div>
        <div><p className="text-sm font-bold text-green-400">{stats.confirmed}</p><p className="text-[10px] text-slate-400">Conf.</p></div>
        <div><p className="text-sm font-bold text-blue-400">{stats.rate}%</p><p className="text-[10px] text-slate-400">Taux</p></div>
      </div>
    </SummaryCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// CARTE 7: APPELS VS R1
// ═══════════════════════════════════════════════════════════════
function AppelsVsR1Summary({ clients, onClick }) {
  const [callStats, setCallStats] = useState(null);
  
  useEffect(() => {
    const load = async () => {
      try {
        const now = new Date();
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(now);
        monday.setDate(diff);
        const params = new URLSearchParams();
        params.append('start_date', monday.toISOString().split('T')[0]);
        params.append('end_date', now.toISOString().split('T')[0]);
        const res = await axios.get(`${API}/ringover/collaborators/stats?${params}`);
        setCallStats(res.data);
      } catch (e) {}
    };
    load();
  }, []);

  const r1Total = useMemo(() => {
    if (!clients) return 0;
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    
    return clients.filter(c => {
      if (!c.datePriseRdv) return false;
      const parts = c.datePriseRdv.split('/');
      if (parts.length !== 3) return false;
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      return date >= monday && date <= now;
    }).length;
  }, [clients]);

  const outboundAnswered = callStats?.team_totals?.outbound_total 
    ? Math.round(callStats.team_totals.outbound_total * (callStats.team_totals.outbound_rate || 0) / 100)
    : 0;
  const conversion = outboundAnswered > 0 ? Math.round(r1Total / outboundAnswered * 100) : 0;

  return (
    <SummaryCard
      icon={BarChart3}
      iconColor="bg-orange-500"
      bgColor="bg-orange-500/10"
      borderColor="border-orange-500/30"
      title="Appels vs R1"
      subtitle="Cette semaine"
      onClick={onClick}
    >
      <div className="grid grid-cols-3 gap-1 text-center">
        <div><p className="text-sm font-bold text-blue-400">{outboundAnswered}</p><p className="text-[10px] text-slate-400">Décr.</p></div>
        <div><p className="text-sm font-bold text-cyan-400">{r1Total}</p><p className="text-[10px] text-slate-400">R1</p></div>
        <div><p className="text-sm font-bold text-orange-400">{conversion}%</p><p className="text-[10px] text-slate-400">Conv.</p></div>
      </div>
    </SummaryCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN: ALERTES CAROUSEL
// ═══════════════════════════════════════════════════════════════
export default function AlertesCarousel({ 
  clients, 
  selectedTelepro, 
  onClientUpdate, 
  authUser,
  CollaboratorEfficiencyComponent,
  R1EfficiencyComponent,
  AppelsVsR1EfficiencyComponent,
  telepros,
  onQualificationAppelClick
}) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [openModal, setOpenModal] = useState(null);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      setContainerWidth(scrollRef.current.offsetWidth);
      setContentWidth(scrollRef.current.scrollWidth);
    }
  }, []);

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < contentWidth - containerWidth - 20;

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 300;
    const newPos = direction === 'left' 
      ? Math.max(0, scrollPosition - scrollAmount)
      : scrollPosition + scrollAmount;
    scrollRef.current.scrollTo({ left: newPos, behavior: 'smooth' });
  };

  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
    setContentWidth(e.target.scrollWidth);
    setContainerWidth(e.target.offsetWidth);
  };

  return (
    <div className="relative mb-6" data-testid="alertes-carousel">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <Bell className="w-4 h-4" />
          Alertes & Efficacité
        </h2>
        <span className="text-xs text-slate-500">Cliquez sur une carte pour voir les détails</span>
      </div>

      {/* Carousel container */}
      <div className="relative group">
        {/* Flèche gauche */}
        <button
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center shadow-lg transition-all -translate-x-3 ${canScrollLeft ? 'opacity-100' : 'opacity-30 cursor-not-allowed'}`}
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>

        {/* Flèche droite */}
        <button
          onClick={() => scroll('right')}
          disabled={!canScrollRight}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center shadow-lg transition-all translate-x-3 ${canScrollRight ? 'opacity-100' : 'opacity-30 cursor-not-allowed'}`}
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>

        {/* Cartes */}
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 px-1"
          onScroll={handleScroll}
          style={{ scrollBehavior: 'smooth' }}
        >
          {/* Carte Nouvel Appel Entrant - Action principale */}
          {onQualificationAppelClick && (
            <SummaryCard
              icon={PhoneIncoming}
              iconColor="bg-emerald-500"
              bgColor="bg-gradient-to-br from-emerald-600/30 to-teal-600/30"
              borderColor="border-emerald-500/50"
              title="Nouveau Lead"
              subtitle="Appel entrant ou autre source"
              onClick={onQualificationAppelClick}
            >
              <div className="text-center mt-1">
                <p className="text-xs text-emerald-300">Cliquez pour démarrer</p>
                <p className="text-[10px] text-slate-400">Qualification & éligibilité</p>
              </div>
            </SummaryCard>
          )}
          <ConfirmationRdvSummary 
            collaborateur={selectedTelepro} 
            onRefresh={onClientUpdate}
            onClick={() => setOpenModal('confirmation')}
          />
          <AppelsMissedSummary 
            selectedTelepro={selectedTelepro}
            onClick={() => setOpenModal('missed')}
          />
          <AppelsQualiteSummary 
            clients={clients}
            onClick={() => setOpenModal('qualite')}
          />
          <EfficaciteAppelsSummary 
            onClick={() => setOpenModal('efficaciteAppels')}
          />
          <RdvPrisSummary 
            clients={clients}
            onClick={() => setOpenModal('rdvPris')}
          />
          <AppelsVsR1Summary 
            clients={clients}
            onClick={() => setOpenModal('appelsVsR1')}
          />
        </div>
      </div>

      {/* MODALS - Affichent les bannières originales DÉPLIÉES */}
      <BannerModal 
        isOpen={openModal === 'confirmation'} 
        onClose={() => setOpenModal(null)}
        title="Alertes Confirmation RDV"
      >
        <AlertesConfirmationRDV collaborateur={selectedTelepro} onRefresh={onClientUpdate} defaultExpanded={true} />
      </BannerModal>

      <BannerModal 
        isOpen={openModal === 'missed'} 
        onClose={() => setOpenModal(null)}
        title="Appels Entrants Manqués"
      >
        <AppelsMissedBanner selectedTelepro={selectedTelepro} defaultExpanded={true} />
      </BannerModal>

      <BannerModal 
        isOpen={openModal === 'qualite'} 
        onClose={() => setOpenModal(null)}
        title="Appels Qualité à Faire"
      >
        <UrgentAppelQualiteBanner clients={clients} onClientUpdate={onClientUpdate} defaultExpanded={true} />
      </BannerModal>

      <BannerModal 
        isOpen={openModal === 'efficaciteAppels'} 
        onClose={() => setOpenModal(null)}
        title="Efficacité Télépros - Appels"
      >
        {CollaboratorEfficiencyComponent}
      </BannerModal>

      <BannerModal 
        isOpen={openModal === 'rdvPris'} 
        onClose={() => setOpenModal(null)}
        title="Efficacité Télépros - RDV Pris"
      >
        {R1EfficiencyComponent}
      </BannerModal>

      <BannerModal 
        isOpen={openModal === 'appelsVsR1'} 
        onClose={() => setOpenModal(null)}
        title="Efficacité - Appels vs R1"
      >
        {AppelsVsR1EfficiencyComponent}
      </BannerModal>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
